import type { Key } from "react";
import React, { useMemo, useState } from "react";
import { useHistory } from "react-router";
import { Row, Table } from "antd";
import { TableProps } from "antd/es/table";
import moment from "moment";
import type { IOfferingGroup } from "../../types/IOfferingGroup";
import { useAppSelector } from "../../../..";
import { checkIsLoading, toDisplayFormat } from "../../../../lib/utils";
import { TableColumn } from "../../../../lib/interfaces";
import ToggleColumns from "../../../../components/ToggleableVersionsTable/ToggleColumns";
import versionsTableUtils from "../../../../lib/utils/versionsTableUtils";
import "./OfferingGroupsSearchTable.less";

interface TableData extends IOfferingGroup {
  key?: Key;
}

interface ComponentProps {
  selectedGroup?: IOfferingGroup;
  setSelectedGroup: React.Dispatch<React.SetStateAction<IOfferingGroup | undefined>>;
  isSearchOpen: boolean;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const OfferingGroupsSearchTable = (props: ComponentProps) => {
  const { setSelectedGroup, selectedGroup, isSearchOpen } = props;
  const history = useHistory();
  const { policiesTypesDicLoading } = useAppSelector((state) => state.policies);
  const { offeringGroups, offeringGroupsLoading, offeringGroupsTypesDic } = useAppSelector(
    (state) => state.offeringGroups
  );

  const isTableLoading = useMemo(
    () => checkIsLoading(offeringGroupsLoading, policiesTypesDicLoading),
    [offeringGroupsLoading, policiesTypesDicLoading]
  );

  const tableData = useMemo((): TableData[] => {
    const data: TableData[] = offeringGroups.map((group) => {
      return {
        ...group,
        key: group.id,
      };
    });
    const sortedData = [...data].sort((a, b) => {
      if (a.id === b.id) {
        return moment(a.fromDate).isBefore(b.fromDate) ? 1 : -1;
      }
      return a.id - b.id;
    });
    return sortedData;
  }, [offeringGroups]);

  const columnsWidths = {
    id: "50px",
    name: "",
    groupingTypeId: "350px",
    fromDate: "150px",
    toDate: "150px",
  };

  const getColumns = (): TableColumn<TableData>[] => [
    {
      key: "id",
      dataIndex: "id",
      title: "ID",
      hidden: true,
      width: columnsWidths.id,
    },
    {
      key: "name",
      dataIndex: "name",
      title: "Наименование",
      hidden: false,
      width: columnsWidths.name,
    },
    {
      key: "groupingTypeId",
      dataIndex: "groupingTypeId",
      title: "Тип группы",
      hidden: true,
      width: columnsWidths.groupingTypeId,
      render: (groupingTypeId) =>
        versionsTableUtils.getDictionaryItemById(offeringGroupsTypesDic, groupingTypeId)?.name,
    },
    {
      key: "fromDate",
      dataIndex: "fromDate",
      title: "Дата с",
      hidden: true,
      width: columnsWidths.fromDate,
      render: (fromDate) => toDisplayFormat(fromDate),
    },
    {
      key: "toDate",
      dataIndex: "toDate",
      title: "Дата по",
      hidden: false,
      width: columnsWidths.toDate,
      render: (toDate) => toDisplayFormat(toDate),
    },
  ];

  const [columns, setColumns] = useState<TableColumn<TableData>[]>(getColumns());

  const toggleGroupSelection = (id?: number) => {
    if (selectedGroup?.id === id) {
      setSelectedGroup(undefined);
    } else {
      const currentGroup = offeringGroups.find((group) => group.id === id);
      setSelectedGroup(currentGroup);
    }
  };

  const onRow: TableProps<TableData>["onRow"] = ({ id }) => ({
    onClick: () => {
      toggleGroupSelection(id);
    },
  });

  return (
    <div className="offering-groups-search-table">
      <Row justify="end" style={{ marginTop: "10px" }}>
        <ToggleColumns
          tableName="offering-groups-search-table"
          columns={columns}
          setColumns={setColumns}
          notHiddenColumnIndex={1}
        />
      </Row>
      <Table
        dataSource={tableData}
        columns={columns.filter(({ hidden }) => !hidden)}
        loading={isTableLoading}
        pagination={false}
        size="small"
        scroll={{
          y: isSearchOpen ? "250px" : "387px",
          x: "600px",
        }}
        rowClassName={({ key }) => (key === selectedGroup?.id ? "ant-table-row-selected" : "")}
        onRow={onRow}
      />
    </div>
  );
};
